import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useConfig } from '@/context/ConfigProvider';
import { useAuthentication } from '@/context/AuthenticationProvider';
import { request } from './client';
import type {
  Audience,
  Category,
  DeviceType,
  DiffLine,
  ExportResult,
  Facets,
  Genre,
  Item,
  ItemFilters,
  ItemsPage,
  ItemUpdateInput,
  Provider,
  Rating,
  SearchResultsPage,
  ShowType,
  Validation,
} from './graphql';

const useClient = () => {
  const config = useConfig();
  const { getIdToken } = useAuthentication();
  return useMemo(
    () => ({ endpoint: config.graphqlEndpoint, getIdToken }),
    [config.graphqlEndpoint, getIdToken],
  );
};

/**
 * Selection sets are split by screen on purpose. The catalogue asks for six fields per row; the detail
 * screen asks for nearly everything. Over-fetching the full item for a 200-row grid is exactly the
 * cost GraphQL exists to avoid.
 */
const LIST_FIELDS = `
  assetId
  version
  provider
  state
  editVersion
  worstValidationLevel
  properties {
    title { original editorial }
    showType { original editorial }
    # Only what the grid thumbnail needs. The url resolver is batched, so a full page of rows costs
    # a couple of Lambda invocations rather than one per image.
    assets { type url }
  }
`;

const DETAIL_FIELDS = `
  assetId
  version
  provider
  providerId
  xmlFilename
  xmlVersion
  state
  error
  editVersion
  exportKey
  exportedAt
  updatedAt
  updatedBy
  worstValidationLevel
  validations { field level ruleId message }
  media { filename size checksum }
  properties {
    title { original editorial }
    titleBrief { original editorial }
    titleSortName { original editorial }
    summaryShort { original editorial }
    summaryMedium { original editorial }
    summaryLong { original editorial }
    year { original editorial }
    rating { original editorial }
    genre { original editorial }
    showType { original editorial }
    audience { original editorial }
    countryOfOrigin { original editorial }
    languages { original editorial }
    audioType { original editorial }
    actors { original editorial }
    director { original editorial }
    runTime { original editorial }
    displayRunTime { original editorial }
    hdContent { original editorial }
    maximumViewingLength { original editorial }
    suggestedPrice { original editorial }
    availableInLocalities { original editorial }
    restrictedDeviceTypes { original editorial }
    seriesOrdinal { original editorial }
    episodeOrdinal { original editorial }
    episodeName { original editorial }
    episodeId { original editorial }
    licensingWindowStart {
      default { original editorial }
      variants { locality original editorial }
    }
    licensingWindowEnd {
      default { original editorial }
      variants { locality original editorial }
    }
    grossPrice {
      default { original editorial }
      variants { locality original editorial }
    }
    netPrice {
      default { original editorial }
      variants { locality original editorial }
    }
    category {
      default { original editorial }
      variants { locality original editorial }
    }
    assets { id content type fromProvider present imageAspectRatio url }
  }
`;

export const useItems = (limit: number, filters?: ItemFilters) => {
  const client = useClient();

  return useQuery({
    queryKey: ['items', limit, filters],
    queryFn: () =>
      request<{ listItems: ItemsPage }>(
        client,
        `query listItems($limit: Int!, $filters: ItemFilters) {
          listItems(limit: $limit, filters: $filters) {
            nextToken
            items { ${LIST_FIELDS} }
          }
        }`,
        { limit, filters },
      ).then((d) => d.listItems),
  });
};

/**
 * Free-text search, backed by OpenSearch. Distinct from `useItems` on purpose: it searches the whole
 * index rather than filtering one page, and returns a real total. `listItems` remains the right call
 * for browsing with no query.
 */
export const useSearchItems = (
  query: string,
  limit: number,
  filters?: ItemFilters,
) => {
  const client = useClient();
  const trimmed = query.trim();

  return useQuery({
    queryKey: ['searchItems', trimmed, limit, filters],
    enabled: trimmed.length > 0,
    // The index lags a write by a moment; refetching on mount would mostly re-fetch identical results.
    staleTime: 5_000,
    queryFn: () =>
      request<{ searchItems: SearchResultsPage }>(
        client,
        `query searchItems($query: String!, $limit: Int!, $filters: ItemFilters) {
          searchItems(query: $query, limit: $limit, filters: $filters) {
            total
            nextToken
            items {
              assetId
              version
              provider
              state
              title
              showType
              worstValidationLevel
              posterUrl
              score
            }
          }
        }`,
        { query: trimmed, limit, filters },
      ).then((d) => d.searchItems),
  });
};

export const useItem = (assetId?: string, version?: string) => {
  const client = useClient();

  return useQuery({
    queryKey: ['item', assetId, version],
    enabled: !!assetId,
    queryFn: () =>
      request<{ item: Item | null }>(
        client,
        `query item($assetId: String!, $version: String) {
          item(assetId: $assetId, version: $version) { ${DETAIL_FIELDS} }
        }`,
        { assetId, version },
      ).then((d) => d.item),
  });
};

export const useRatings = () => {
  const client = useClient();

  return useQuery({
    queryKey: ['ratings'],
    // Vocabularies change rarely; no point refetching them per screen.
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      request<{ ratings: Rating[] }>(
        client,
        `query { ratings { id name order } }`,
      ).then((d) => d.ratings),
  });
};

/**
 * The controlled vocabularies, all on the same footing.
 *
 * One factory rather than six near-identical hooks: they differ only in the query name and the
 * fields each type carries, and a copy per vocabulary is where the next one gets forgotten. The
 * shared `staleTime` matters as much — a detail screen opens six of these to populate its dropdowns,
 * and they change about once a quarter.
 */
const VOCABULARY_STALE_TIME = 5 * 60 * 1000;

const vocabularyHook =
  <T>(name: string, fields: string) =>
  () => {
    const client = useClient();

    return useQuery({
      queryKey: [name],
      staleTime: VOCABULARY_STALE_TIME,
      queryFn: () =>
        request<Record<string, T[]>>(
          client,
          `query { ${name} { ${fields} } }`,
        ).then((d) => d[name]),
    });
  };

/**
 * Filter values that actually occur in the catalogue, with counts.
 *
 * Not a vocabulary: those are what an admin has configured, which is a different question from what
 * the content actually contains — real packages routinely carry values nobody configured. The
 * catalogue filters ask the second question, so they read the index.
 */
export const useFacets = () => {
  const client = useClient();

  return useQuery({
    queryKey: ['facets'],
    // Only changes when content is ingested, and a stale count is harmless in a dropdown.
    staleTime: 60 * 1000,
    queryFn: () =>
      request<{ facets: Facets }>(
        client,
        `query {
          facets {
            provider { value count }
            state { value count }
            showType { value count }
            validationLevel { value count }
            genre { value count }
            language { value count }
          }
        }`,
      ).then((d) => d.facets),
  });
};

export const useGenres = vocabularyHook<Genre>('genres', 'id name');
export const useShowTypes = vocabularyHook<ShowType>(
  'showTypes',
  'id name isSeries',
);
export const useAudiences = vocabularyHook<Audience>('audiences', 'id name');
export const useDeviceTypes = vocabularyHook<DeviceType>(
  'deviceTypes',
  'id name',
);
export const useProviders = vocabularyHook<Provider>(
  'providers',
  'id name prefix providerId',
);
export const useCategories = vocabularyHook<Category>(
  'categories',
  'path name',
);

/**
 * Vocabulary writes, parameterised by mutation name for the same reason the reads are.
 *
 * The write is an **upsert keyed by id**, which is why the config screens refuse to let an existing
 * id be edited: changing it would silently create a second entry rather than rename the first.
 */
export const useSetVocabularyEntry = (mutation: string, queryName: string) => {
  const client = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      request<Record<string, unknown>>(
        client,
        `mutation set($input: ${
          INPUT_TYPES[mutation] ?? 'NamedInput'
        }!) { ${mutation}(input: $input) { id } }`,
        { input },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryName] }),
  });
};

export const useDeleteVocabularyEntry = (
  mutation: string,
  queryName: string,
) => {
  const client = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      request<Record<string, unknown>>(
        client,
        `mutation del($id: ID!) { ${mutation}(id: $id) { id } }`,
        { id },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryName] }),
  });
};

/**
 * GraphQL needs the concrete input type name in the operation, and most vocabularies share
 * `NamedInput`. Only the two that carry extra fields differ.
 */
const INPUT_TYPES: Record<string, string> = {
  setShowType: 'ShowTypeInput',
  setProvider: 'ProviderInput',
};

export const useUpdateItem = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ItemUpdateInput) =>
      request<{ updateItem: Item }>(
        client,
        `mutation updateItem($input: ItemUpdateInput!) {
          updateItem(input: $input) { ${DETAIL_FIELDS} }
        }`,
        { input },
      ).then((d) => d.updateItem),
    onSuccess: (item) => {
      queryClient.setQueryData(['item', item.assetId, item.version], item);
      void queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });
};

export const useRevertField = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: {
      assetId: string;
      version: string;
      field: string;
      currentVersion: number;
    }) =>
      request<{ revertField: Item }>(
        client,
        `mutation revertField($assetId: String!, $version: String!, $field: String!, $currentVersion: Int!) {
          revertField(assetId: $assetId, version: $version, field: $field, currentVersion: $currentVersion) {
            ${DETAIL_FIELDS}
          }
        }`,
        vars,
      ).then((d) => d.revertField),
    onSuccess: (item) => {
      queryClient.setQueryData(['item', item.assetId, item.version], item);
      void queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });
};

export const useRevalidateItem = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { assetId: string; version: string }) =>
      request<{ revalidateItem: Validation[] }>(
        client,
        `mutation revalidateItem($assetId: String!, $version: String!) {
          revalidateItem(assetId: $assetId, version: $version) {
            field level ruleId message
          }
        }`,
        vars,
      ).then((d) => d.revalidateItem),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['item', vars.assetId, vars.version],
      });
    },
  });
};

export const useExportItem = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { assetId: string; version: string }) =>
      request<{ exportItems: ExportResult }>(
        client,
        `mutation exportItems($items: [ExportInput!]!) {
          exportItems(items: $items) {
            accepted
            rejected { assetId version reason }
          }
        }`,
        { items: [vars] },
      ).then((d) => d.exportItems),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['item', vars.assetId, vars.version],
      });
      void queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });
};

/**
 * Soft delete and its inverse.
 *
 * Both invalidate the item *and* the catalogue list: the row has to leave (or rejoin) the catalogue, and
 * the search index is updated asynchronously off the table stream, so the list query is the only thing
 * that reflects it promptly.
 */
const useItemDeletion = (operation: 'deleteItem' | 'restoreItem') => {
  const client = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: {
      assetId: string;
      version: string;
      currentVersion: number;
    }) =>
      request<Record<typeof operation, Item>>(
        client,
        `mutation ${operation}($assetId: String!, $version: String!, $currentVersion: Int!) {
          ${operation}(assetId: $assetId, version: $version, currentVersion: $currentVersion) {
            assetId version deleted editVersion
          }
        }`,
        vars,
      ).then((d) => d[operation]),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['item', vars.assetId, vars.version],
      });
      void queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });
};

export const useDeleteItem = () => useItemDeletion('deleteItem');
export const useRestoreItem = () => useItemDeletion('restoreItem');

export const usePreviewXml = (assetId?: string, version?: string) => {
  const client = useClient();

  return useQuery({
    queryKey: ['previewXml', assetId, version],
    enabled: !!assetId && !!version,
    queryFn: () =>
      request<{ previewXml: string }>(
        client,
        `query previewXml($assetId: String!, $version: String!) {
          previewXml(assetId: $assetId, version: $version)
        }`,
        { assetId, version },
      ).then((d) => d.previewXml),
  });
};

export const useXmlDiff = (assetId?: string, version?: string) => {
  const client = useClient();

  return useQuery({
    queryKey: ['xmlDiff', assetId, version],
    enabled: !!assetId && !!version,
    queryFn: () =>
      request<{ xmlDiff: DiffLine[] }>(
        client,
        `query xmlDiff($assetId: String!, $version: String!) {
          xmlDiff(assetId: $assetId, version: $version) { op text }
        }`,
        { assetId, version },
      ).then((d) => d.xmlDiff),
  });
};

/**
 * Replace an item's artwork.
 *
 * Three steps, because the file never passes through VMS: ask for a presigned URL, PUT the bytes
 * straight to S3, then tell VMS what landed so it can read the size and checksum back off the object
 * and attach it to the item. Exposed as one hook so a caller cannot perform two of the three and leave
 * an item referencing a file nothing recorded — the state that makes an export fail with a message
 * about a missing media item.
 *
 * The PUT deliberately bypasses `request`: it goes to S3, not to AppSync, and must carry no
 * Authorization header — a presigned URL is already authorised, and adding one makes S3 reject it.
 */
export const useReplaceAsset = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      assetId: string;
      version: string;
      currentVersion: number;
      type: string;
      file: File;
      imageAspectRatio?: string;
    }) => {
      const { assetUploadUrls } = await request<{
        assetUploadUrls: { targetFilename: string; uploadUrl: string }[];
      }>(
        client,
        `query assetUploadUrls($files: [FileUrlRequest!]!) {
          assetUploadUrls(files: $files) { filename targetFilename uploadUrl }
        }`,
        {
          files: [
            {
              assetId: vars.assetId,
              version: vars.version,
              filename: vars.file.name,
              contentType: vars.file.type,
            },
          ],
        },
      );

      const target = assetUploadUrls[0];
      if (!target) {
        throw new Error('No upload URL was issued for this file.');
      }

      const put = await fetch(target.uploadUrl, {
        method: 'PUT',
        body: vars.file,
        headers: vars.file.type ? { 'Content-Type': vars.file.type } : {},
      });
      if (!put.ok) {
        // S3 answers with an XML error body; the status is the part worth showing.
        throw new Error(
          `Upload failed (${put.status}). The link may have expired — try again.`,
        );
      }

      return request<{ registerAssetUpload: Item }>(
        client,
        `mutation registerAssetUpload($input: AssetUploadInput!) {
          registerAssetUpload(input: $input) { ${DETAIL_FIELDS} }
        }`,
        {
          input: {
            assetId: vars.assetId,
            version: vars.version,
            currentVersion: vars.currentVersion,
            targetFilename: target.targetFilename,
            type: vars.type,
            imageAspectRatio: vars.imageAspectRatio,
          },
        },
      ).then((d) => d.registerAssetUpload);
    },
    onSuccess: (item) => {
      queryClient.setQueryData(['item', item.assetId, item.version], item);
      void queryClient.invalidateQueries({ queryKey: ['items'] });
      // The XML changes too — a replaced asset rewrites the Content node and its checksum.
      void queryClient.invalidateQueries({ queryKey: ['xmlDiff'] });
      void queryClient.invalidateQueries({ queryKey: ['previewXml'] });
    },
  });
};
